import { Box, Grid, Typography } from "@mui/material";
import * as React from "react";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "hooks/use_backend";
import { REACT_QUERY_KEYS } from "enums/react_query_key_enum";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LimitAdornment } from "components/adornments/limit_adornment";
import HelpedAdornedTextField from "components/general/helped_adorned_text_field";
import PhaseSelect from "components/device/selects/phase_select";
import { EditableCard } from "components/general/editable_card";
import Snackbar from "scripts/snackbar";
import {commandUpdateSiteConfig} from "scripts/api_v2/commands_client";
import {useParams} from "react-router-dom";
import {useSiteData} from "hooks/use_site";

export default function ConnectionCard({ required_privilege, ...props }) {
  const params = useParams();
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();

  const [editingConfig, setEditingConfig] = React.useState(false);
  const [configEdits, setConfigEdits] = React.useState({});
  const [confCopy, setConfCopy] = React.useState({});

  let { data: siteData, error, isLoading, refetch } = useSiteData();

  const queryClient = useQueryClient();

  const {mutate: configMutation, isPending: isMutating} = useMutation({
        mutationFn: async ({commit, configEdits, mutatedField, mutatedValue, isString}
        ) => {
            if (!commit) {
                // If commit is false, we don't perform the mutation
                return await Promise.resolve(); // Return a resolved promise to avoid rejection
            } else { // Do the actual commit
                return await commandUpdateSiteConfig(backend.fetch, params.site_id, configEdits);
            }
        },
        onMutate: async ({commit, configEdits, mutatedField, mutatedValue, isString}) => {
            //If we always commit, consider using this to snapshot the previous value
            //const previousSiteData = queryClient.getQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, siteId])
            const oldSiteData = {...siteData, "config": confCopy}

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });

      // Optimistically update to the new value(s)
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const updated = { ...prev.config };
        if (commit) {
          Object.values(configEdits).forEach((edit) => {
            updated[edit.field] = edit.value;
          });
        } else {
          updated[mutatedField] = isString ? mutatedValue.toString() : mutatedValue;
        }
        return { ...prev, config: updated };
      });

      return { oldSiteData };
    },
    onError: (err, variables, context) => {
      //Using handleCancel because its reused, but we should prefer to use the context.oldSiteData to rollback...
      handleCancelConfig();
      Snackbar.error("Could not update config: " + err);
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      const { commit, configEdits } = variables;
      if (commit) {
        await refetch();
        if (!error) {
          // The refetch above can race with the backend applying the change and briefly
          // return stale data; keep showing the values we just saved until a later,
          // genuinely up-to-date refresh confirms them.
          queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
            if (!prev) return prev;
            const updated = { ...prev.config };
            Object.values(configEdits).forEach((edit) => {
              updated[edit.field] = edit.value;
            });
            return { ...prev, config: updated };
          });
        }
        setConfigEdits({});
        setEditingConfig(false);
      }
    },
  });

  function handleEditConfig() {
    setConfCopy(siteData ? siteData.config : {});
    setEditingConfig(true);
  }

  function handleCancelConfig() {
    queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
      return { ...prev, config: confCopy };
    });
    setConfigEdits({});
    setEditingConfig(false);
  }

  function handleConfigChange(field, value, isString = false) {
      const processedValue = isString ? value.toString() : (typeof (value) == "boolean" ||
          typeof (value) == "number" ? value : (value.length > 0 ? parseInt(value) : 0))
      const updated = {...configEdits};
      updated[field] = {field: field, value: processedValue, prev_value: confCopy[field]};
      setConfigEdits(updated)

      const mutationData = {
          commit: false,
          configEdits: updated, mutatedField: field, mutatedValue: processedValue, isString: isString
      };

      configMutation(mutationData);
  }

  async function commitConfigChange() {

      let processedConfigEdits = {...configEdits};
    
      if (Object.values(configEdits).length === 0) {
          handleCancelConfig()
      } else {
          const mutationData = {
              commit: true,
              configEdits: processedConfigEdits, mutatedField: null, mutatedValue: null, isString: false
          };
          configMutation(mutationData);
      }
  }

    return <EditableCard editable title="Installation" width={"100%"} elevation={0}
                         editing={editingConfig} handleEdit={handleEditConfig} handleSave={commitConfigChange}
                         handleCancel={handleCancelConfig} permissionMap={permissionMap}
                         required_privilege={required_privilege} {...props}>
        {siteData && siteData.config ?
            <Grid container sx={{mt: 1}}>
                <Grid md={6} xs={12} item width={"80%"}
                      sx={{display: "flex", alignItems: "center", justifyContent: "center"}} px={2} py={2}>
                    <PhaseSelect label={"Connection type"} fullWidth disabled={!editingConfig}
                                 value={siteData["config"]["connection_type"]}
                                 onChange={(e) => handleConfigChange("connection_type", e.target.value, false)}/>
                </Grid>
                <Grid md={6} xs={0} item width={"80%"}
                      sx={{display: "flex", alignItems: "center", justifyContent: "center"}} px={2} py={2}/>
                <Grid md={6} xs={12} item width={"80%"}
                                            sx={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}} px={2} py={2}>
                    <HelpedAdornedTextField fullWidth adornment={<LimitAdornment
                        limit={siteData["config"]["max_grid_offtake_sp"]}
                        connectionType={siteData["config"]["connection_type"]}/>} key="offtakelim"
                                                                                        label="Max grid offtake setpoint (W)"
                                            info="The maximum power in Watts that can be taken from grid"
                                            type={"text"} value={siteData["config"]["max_grid_offtake_sp"]}
                                            disabled={!editingConfig}
                                            onChange={(e) => handleConfigChange("max_grid_offtake_sp", e.target.value, false)}/>
                </Grid>
                <Grid md={6} xs={12} item width={"80%"}
                      sx={{display: "flex", alignItems: "center", justifyContent: "center"}} px={2} py={2}>
                    <HelpedAdornedTextField fullWidth adornment={<LimitAdornment
                        limit={siteData["config"]["max_grid_inject_sp"]}
                        connectionType={siteData["config"]["connection_type"]}/>} key="injectlim"
                                            label={"Max grid inject setpoint (W)"}
                                            info="The maximum power in Watts that can be injected to grid"
                                            type={"text"} value={siteData["config"]["max_grid_inject_sp"]}
                                            disabled={!editingConfig}
                                            onChange={(e) => handleConfigChange("max_grid_inject_sp", e.target.value, false)}/>
                </Grid>
            </Grid> : <Typography>No data available</Typography>}
    </EditableCard>
}
